import React, { useState } from 'react';
import axios from 'axios';
import { C } from './LandingPartes';

const INDICATIVOS = ['+57', '+1', '+52', '+34', '+51', '+593', '+58', '+56', '+507', '+54', '+591', '+502'];
const VACIO = { nombre: '', correo: '', indicativo: '+57', telefono: '', consentimiento_datos: false, consentimiento_whatsapp: false, company: '' };
const ROJO = '#9B2C2C';

const ErrorCampo = ({ children, testId, id }) => children
  ? <p id={id} className="boda-sans mt-2 text-[0.9375rem] font-medium" style={{ color: ROJO }} role="alert" data-testid={testId}>{children}</p> : null;

const textoExito = ({ yaRegistrado, noAplica, mensaje }) => {
  if (noAplica) return ['Recibimos tus datos', mensaje];
  if (yaRegistrado) return ['¡Ya estabas registrado!', 'Ya teníamos tus datos con este correo. Te contactaremos pronto para coordinar tu asesoría y tu beneficio.'];
  return ['¡Gracias por registrarte!', 'Recibimos tus datos. Te contactaremos para coordinar tu asesoría y tu beneficio.'];
};

const Exito = ({ resultado, waHref }) => {
  const [titulo, texto] = textoExito(resultado);
  return (
    <div className="mx-auto max-w-xl text-center" data-testid={resultado.noAplica ? 'registro-no-aplica' : 'registro-exito'} role="status">
      <p className="text-[clamp(1.75rem,6vw,2.3rem)] font-semibold" style={{ color: C.tinta }}>{titulo}</p>
      <p className="boda-sans mx-auto mt-3 max-w-md text-[1.0625rem] leading-relaxed" style={{ color: C.suave }} data-testid="registro-resultado-texto">{texto}</p>
      {waHref && <a href={waHref} target="_blank" rel="noopener noreferrer" className="boda-boton mt-7 w-full sm:w-auto" data-testid="registro-continuar-whatsapp">Continuar por WhatsApp</a>}
    </div>
  );
};

const Campo = ({ id, label, error, testId, children }) => (
  <div className="mb-6">
    <label className="boda-etiqueta" htmlFor={id}>{label}</label>
    <div className="mt-2">{children}</div>
    <ErrorCampo id={`${id}-error`} testId={`registro-error-${testId}`}>{error}</ErrorCampo>
  </div>
);

const Casilla = ({ checked, onChange, testId, children }) => (
  <label className="boda-sans flex cursor-pointer items-start gap-3 rounded-md py-2 text-left text-[0.9375rem] leading-relaxed" style={{ color: C.carbon }}>
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="boda-check" data-testid={testId} />
    <span>{children}</span>
  </label>
);

const Consentimientos = ({ datos, set, errores, marca, correo, politicaUrl }) => (
  <div className="mb-6 space-y-2">
    <Casilla checked={datos.consentimiento_datos} onChange={v => set({ consentimiento_datos: v })} testId="registro-consentimiento-datos">
      Autorizo el tratamiento de mis datos personales para gestionar mi solicitud de asesoría y ser contactado(a) por {marca}.
      {correo && <> Conoce y ejerce tus derechos escribiendo a <a href={`mailto:${correo}`} className="boda-enlace">{correo}</a>.</>}
    </Casilla>
    <ErrorCampo testId="registro-error-consentimiento">{errores.consentimiento_datos}</ErrorCampo>
    <Casilla checked={datos.consentimiento_whatsapp} onChange={v => set({ consentimiento_whatsapp: v })} testId="registro-consentimiento-whatsapp">
      <span className="font-semibold">(Opcional)</span> Autorizo a {marca} a enviarme mensajes por WhatsApp sobre asesorías, productos y beneficios, de acuerdo con la{' '}
      {politicaUrl ? <a href={politicaUrl} target="_blank" rel="noopener noreferrer" className="boda-enlace">Política de Tratamiento de Datos</a> : 'Política de Tratamiento de Datos'}.
    </Casilla>
  </div>
);

const mensajeErrorRed = (status) => ({
  429: 'Demasiados intentos. Inténtalo de nuevo en unos minutos.',
  404: 'Esta página ya no está disponible para registros.',
}[status] || 'No pudimos completar el registro en línea. Inténtalo de nuevo o escríbenos por WhatsApp.');

export default function FormularioBeneficio({ endpoint, waHref, contacto, titulo, mensaje, vistaPrevia = false }) {
  const [datos, setDatos] = useState(VACIO);
  const [estado, setEstado] = useState({ tipo: 'idle' });
  const set = (cambios) => setDatos(prev => ({ ...prev, ...cambios }));
  const errores = estado.tipo === 'invalido' ? estado.campos : {};
  const marca = contacto?.marca_nombre || 'Juan Pablo Murillo';
  const enviando = estado.tipo === 'enviando';

  const enviar = async (e) => {
    e.preventDefault();
    if (vistaPrevia || enviando) return;
    if (!datos.consentimiento_datos) {
      setEstado({ tipo: 'invalido', campos: { consentimiento_datos: 'Para continuar, marca la autorización de tratamiento de datos.' } });
      return;
    }
    setEstado({ tipo: 'enviando' });
    try {
      const res = await axios.post(endpoint, datos);
      setEstado({ tipo: 'exito', yaRegistrado: !!res.data?.ya_registrado, noAplica: !!res.data?.no_aplica, mensaje: res.data?.mensaje });
    } catch (err) {
      const status = err.response?.status;
      if (status === 422 && err.response.data?.fields) setEstado({ tipo: 'invalido', campos: err.response.data.fields });
      else setEstado({ tipo: 'error', mensaje: mensajeErrorRed(status) });
    }
  };

  if (estado.tipo === 'exito') return <Exito resultado={estado} waHref={waHref} />;

  const invalido = (campo) => (errores[campo] ? 'true' : 'false');
  return (
    <div className="mx-auto max-w-xl text-center" data-testid="registro-formulario">
      <h2 className="text-[clamp(1.75rem,6vw,2.3rem)] font-semibold leading-tight" style={{ color: C.tinta }}>{titulo}</h2>
      <p className="boda-sans mx-auto mt-3 max-w-md text-[1.0625rem] leading-relaxed" style={{ color: C.suave }}>{mensaje}</p>
      <form onSubmit={enviar} className="mx-auto mt-8 max-w-md text-left" noValidate>
        <div aria-hidden className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
          <label>No llenar<input tabIndex={-1} autoComplete="off" value={datos.company} onChange={e => set({ company: e.target.value })} /></label>
        </div>
        <Campo id="b-nombre" label="Nombre completo" error={errores.nombre} testId="nombre">
          <input id="b-nombre" type="text" value={datos.nombre} onChange={e => set({ nombre: e.target.value })} className="boda-campo"
            placeholder="Ej.: María Fernanda López" autoComplete="name" aria-invalid={invalido('nombre')} aria-describedby="b-nombre-error" data-testid="registro-nombre" />
        </Campo>
        <Campo id="b-correo" label="Correo electrónico" error={errores.correo} testId="correo">
          <input id="b-correo" type="email" inputMode="email" value={datos.correo} onChange={e => set({ correo: e.target.value })} className="boda-campo"
            placeholder="tu@correo.com" autoComplete="email" aria-invalid={invalido('correo')} aria-describedby="b-correo-error" data-testid="registro-correo" />
        </Campo>
        <Campo id="b-telefono" label="Teléfono celular" error={errores.telefono || errores.indicativo} testId="telefono">
          <div className="flex gap-2">
            <select aria-label="Indicativo de país" value={datos.indicativo} onChange={e => set({ indicativo: e.target.value })}
              className="boda-campo !w-[6.5rem] shrink-0" data-testid="registro-indicativo">
              {INDICATIVOS.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
            <input id="b-telefono" type="tel" inputMode="tel" value={datos.telefono} onChange={e => set({ telefono: e.target.value })} className="boda-campo min-w-0"
              placeholder="300 000 0000" autoComplete="tel-national" aria-invalid={invalido('telefono')} aria-describedby="b-telefono-error" data-testid="registro-telefono" />
          </div>
        </Campo>
        <Consentimientos datos={datos} set={set} errores={errores} marca={marca} correo={contacto?.correo_contacto} politicaUrl={contacto?.politica_url} />
        {estado.tipo === 'error' && <p className="boda-sans mb-4 text-[0.9375rem] font-medium" style={{ color: ROJO }} role="alert" data-testid="registro-error-general">{estado.mensaje}</p>}
        <button type="submit" disabled={vistaPrevia || enviando} aria-busy={enviando ? 'true' : 'false'} className="boda-boton w-full" data-testid="registro-enviar">
          {enviando && <span className="boda-spinner" aria-hidden />}
          {enviando ? 'Enviando…' : 'Solicitar mi beneficio'}
        </button>
        <p className="boda-sans mt-3 text-center text-sm" style={{ color: C.suave }}>
          {vistaPrevia ? 'Vista previa: el formulario se activa en la página real.' : 'Te contactaremos solo para coordinar tu asesoría.'}
        </p>
      </form>
    </div>
  );
}
